import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paperclip, Loader2, FileText, Image as ImageIcon, File as FileIcon,
  ScanLine, ChevronRight, ExternalLink,
} from 'lucide-react';
import AttachmentPreviewModal from '@/components/AttachmentPreviewModal';

/**
 * RecentUploadsWidget — dashboard tile showing the last N attachments
 * uploaded across the system. Each row links to its parent record
 * (Client/Property/Appraisal). Scanned-Notes rows surface a
 * "Resume scan" nudge (Phase D placeholder — will trigger OCR auto-
 * populate once Gemini Vision integration ships).
 */
const iconFor = (mime) => {
  if (!mime) return FileIcon;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf' || mime.startsWith('text/')) return FileText;
  return FileIcon;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function RecentUploadsWidget({ api }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/attachments/recent?limit=5')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ items: [], pending_ocr_count: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  const jumpToScope = (att) => {
    const ctx = att.context || {};
    if (att.scope === 'appraisal' && ctx.appraisal_id) {
      navigate(`/appraisals/${ctx.appraisal_id}?tab=Attachments`);
    } else if (att.scope === 'property' && ctx.property_id) {
      navigate(`/properties/${ctx.property_id}`);
    } else if (att.scope === 'client' && ctx.client_id) {
      navigate(`/clients/${ctx.client_id}`);
    }
  };

  if (loading) {
    return (
      <div data-testid="recent-uploads-loading" className="bg-white border border-[#E2E2DF] rounded-md p-6">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin" /> Loading recent uploads…
        </div>
      </div>
    );
  }
  if (!data || data.items.length === 0) return null;

  return (
    <div data-testid="recent-uploads-widget" className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip size={15} className="text-slate-500 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 font-[Manrope]">Recently Uploaded</h3>
            <p className="text-[11px] text-slate-500 truncate">
              Last {data.items.length} document{data.items.length === 1 ? '' : 's'} across the system
              {data.pending_ocr_count > 0 && (
                <span className="ml-1.5 text-orange-600 font-semibold">
                  · {data.pending_ocr_count} scan{data.pending_ocr_count === 1 ? '' : 's'} pending OCR
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          data-testid="recent-uploads-search-link"
          onClick={() => navigate('/documents')}
          className="text-xs text-orange-700 hover:text-orange-900 font-semibold inline-flex items-center gap-1"
        >
          All <ChevronRight size={12} />
        </button>
      </div>

      <div className="divide-y divide-slate-100">
        {data.items.map((att) => {
          const Icon = iconFor(att.mime_type);
          const ctx = att.context || {};
          return (
            <div
              key={att._id}
              data-testid={`recent-upload-row-${att._id}`}
              className="px-4 py-2.5 hover:bg-slate-50/60 group"
            >
              <div className="flex items-start gap-3">
                <Icon size={14} className="text-slate-400 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewing(att)}
                      data-testid={`recent-upload-preview-${att._id}`}
                      className="text-sm font-semibold text-slate-800 hover:text-orange-700 truncate text-left"
                    >
                      {att.original_filename}
                    </button>
                    {att.needs_ocr && (
                      <span
                        data-testid={`recent-upload-needs-ocr-${att._id}`}
                        className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-200 inline-flex items-center gap-0.5 flex-shrink-0"
                        title="This scan hasn't been processed by OCR yet. Click 'Resume scan' to extract fields once Phase D ships."
                      >
                        <ScanLine size={9} /> Scan
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">
                    <span className="text-slate-700 font-medium">{att.kind}</span>
                    {ctx.client_name && <> · {ctx.client_name}</>}
                    {ctx.property_label && <> · {ctx.property_label}</>}
                    {ctx.appraisal_no && <> · A{ctx.appraisal_no}</>}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {timeAgo(att.uploaded_at)} · by {att.uploaded_by || '—'}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {att.needs_ocr && (
                    <button
                      data-testid={`recent-upload-resume-scan-${att._id}`}
                      onClick={() => setPreviewing(att)}
                      className="text-[10px] bg-orange-50 border border-orange-300 text-orange-700 hover:bg-orange-100 rounded-md px-2 py-0.5 font-semibold whitespace-nowrap"
                      title="Phase D placeholder — will trigger Gemini Vision OCR auto-populate once shipped."
                    >
                      Resume scan
                    </button>
                  )}
                  <button
                    onClick={() => jumpToScope(att)}
                    className="text-[10px] text-slate-500 hover:text-slate-900 hover:underline inline-flex items-center gap-0.5"
                  >
                    Open <ExternalLink size={9} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {previewing && (
        <AttachmentPreviewModal
          api={api}
          attachment={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}
